import { useMemo } from 'react';

import { useCurrentuser } from '@uplift/contexts';
import { licenseRequiresCollabAgreement } from '@uplift/ui-v2';
import { useProviderLicensesCollabAgreementsQuery, useProviderLicensesStatusQuery, useStatesQuery } from '@uplift/queries';

import { PracticeStateRow, PracticeStateStatus } from './components/PracticeStatesTable';
import { License, LicenseCollabAgreement, State } from '@uplift/api';

const deriveStatus = (state: State, license: License, activeCollabAgreement: LicenseCollabAgreement | undefined): PracticeStateStatus => {
  if (license.live) {
    return 'live';
  }

  if (licenseRequiresCollabAgreement(state, license)) {
    if (!activeCollabAgreement) {
      return 'getStarted';
    }

    return 'inReview';
  }

  return 'goLive';
};

export const usePracticeStatesData = () => {
  const me = useCurrentuser();
  const { data: licenses, isLoading: licensesLoading } = useProviderLicensesStatusQuery(me?.id);
  const { data: states = [], isLoading: statesLoading } = useStatesQuery();
  const { data: collabAgreementsById, isLoading: collabAgreementsLoading } = useProviderLicensesCollabAgreementsQuery(me?.id);

  const statesById = useMemo(
    () => states.reduce((map, state) => ({ ...map, [state.id]: state }), {} as Record<string, (typeof states)[number]>),
    [ states ]
  );

  const rows = useMemo<PracticeStateRow[]>(() => {
    if (!licenses) return [];

    return licenses
      .filter(license => !!license.stateId)
      .map((license): PracticeStateRow => {
        const state = statesById[license.stateId];
        const collabAgreements = collabAgreementsById?.[license.id] ?? [];
        const activeCollabAgreement = collabAgreements.find(agreement => agreement.archivedAt === null);

        const requiresCollabAgreement = licenseRequiresCollabAgreement(state, license);

        return {
          id: license.id ?? license.stateId,
          stateId: license.stateId,
          stateName: state?.fullName,
          requiresCollabAgreement,
          activeCollabAgreement,
          status: deriveStatus(state, license, activeCollabAgreement),
        };
      });
  }, [ licenses, statesById, collabAgreementsById ]);

  return { rows, isLoading: licensesLoading || statesLoading || collabAgreementsLoading };
};
